import { motion } from 'framer-motion';
import { useIsMobile } from '../hooks/useIsMobile';

export default function SpaceBackground() {
  const isMobile = useIsMobile();
  
  // On mobile: make everything static except rocket
  const smallStarCount = isMobile ? 15 : 100;
  const largeStarCount = isMobile ? 3 : 30;
  const asteroidCount = isMobile ? 0 : 20; // No asteroids on mobile

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Deep space gradient base */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#000510] via-[#0a0e27] to-[#1a1f3a]" />
      
      {/* Stars - small twinkling stars (static on mobile) */}
      <div className="absolute inset-0">
        {[...Array(smallStarCount)].map((_, i) => (
          <motion.div
            key={`star-${i}`}
            className="absolute w-1 h-1 bg-white rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.7 + 0.3,
            }}
            animate={isMobile ? {} : {
              opacity: [Math.random() * 0.5, 1, Math.random() * 0.5],
            }}
            transition={isMobile ? {} : {
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Larger stars (static on mobile) */}
      <div className="absolute inset-0">
        {[...Array(largeStarCount)].map((_, i) => (
          <motion.div
            key={`star-large-${i}`}
            className="absolute w-2 h-2 bg-white rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.5 + 0.5,
            }}
            animate={isMobile ? {} : {
              opacity: [0.3, 1, 0.3],
              scale: [1, 1.2, 1],
            }}
            transition={isMobile ? {} : {
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      {/* Distant galaxy/nebula glow (static on mobile) */}
      <motion.div
        className="absolute top-10 right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-[60px]"
        animate={isMobile ? {} : {
          opacity: [0.2, 0.4, 0.2],
          scale: [1, 1.1, 1],
        }}
        transition={isMobile ? {} : {
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        style={isMobile ? { opacity: 0.3 } : {}}
      />

      {/* Another nebula glow (static on mobile) */}
      <motion.div
        className="absolute bottom-20 left-20 w-[500px] h-[500px] bg-blue-500/15 rounded-full blur-[80px]"
        animate={isMobile ? {} : {
          opacity: [0.15, 0.3, 0.15],
          scale: [1, 1.15, 1],
        }}
        transition={isMobile ? {} : {
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        style={isMobile ? { opacity: 0.2 } : {}}
      />

      {/* Earth - bottom right with atmosphere glow (static rotation on mobile) */}
      <motion.div
        className="absolute -bottom-40 -right-40 w-[600px] h-[600px]"
        animate={isMobile ? {} : {
          rotate: [0, 360],
        }}
        transition={isMobile ? {} : {
          duration: 120,
          repeat: Infinity,
          ease: 'linear',
        }}
      >
        {/* Earth's atmosphere glow - multiple layers */}
        <div className="absolute inset-0 rounded-full bg-gradient-radial from-blue-400/30 via-blue-500/20 to-transparent blur-2xl" />
        <div className="absolute -inset-4 rounded-full bg-gradient-radial from-cyan-400/20 via-blue-400/10 to-transparent blur-3xl" />
        
        {/* Earth itself */}
        <div className="absolute inset-12 rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-blue-900 shadow-[inset_-20px_-20px_60px_rgba(0,0,0,0.5)]">
          {/* Oceans - multiple shades */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400/30 via-transparent to-blue-700/40" />
          
          {/* Continents - more detailed landmasses */}
          <div className="absolute top-16 left-20 w-32 h-24 bg-green-600/60 rounded-[40%] blur-[1px]" />
          <div className="absolute top-12 left-36 w-28 h-20 bg-green-700/50 rounded-[35%] blur-[1px] rotate-12" />
          <div className="absolute top-48 right-24 w-40 h-32 bg-green-600/65 rounded-[45%] blur-[1px] -rotate-6" />
          <div className="absolute bottom-28 left-16 w-36 h-28 bg-green-700/55 rounded-[38%] blur-[1px] rotate-45" />
          <div className="absolute bottom-16 right-32 w-24 h-20 bg-green-600/50 rounded-[42%] blur-[1px]" />
          
          {/* Ice caps */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-16 bg-white/80 rounded-full blur-sm" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-28 h-14 bg-white/70 rounded-full blur-sm" />
          
          {/* Clouds - static on mobile */}
          <motion.div
            className="absolute inset-0 rounded-full"
            animate={isMobile ? {} : {
              rotate: [0, -360],
            }}
            transition={isMobile ? {} : {
              duration: 150,
              repeat: Infinity,
              ease: 'linear',
            }}
          >
            <div className="absolute top-20 left-32 w-24 h-16 bg-white/40 rounded-full blur-md" />
            <div className="absolute top-52 right-28 w-32 h-20 bg-white/30 rounded-full blur-md" />
            <div className="absolute bottom-32 left-20 w-28 h-18 bg-white/35 rounded-full blur-md" />
            <div className="absolute top-36 right-40 w-20 h-14 bg-white/25 rounded-full blur-md" />
            <div className="absolute bottom-48 right-16 w-26 h-16 bg-white/30 rounded-full blur-md" />
          </motion.div>
          
          {/* City lights on night side (subtle glow) */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            <div className="absolute top-1/4 right-8 w-2 h-1 bg-yellow-300/60 blur-[2px] rounded-full" />
            <div className="absolute top-1/3 right-12 w-1 h-1 bg-yellow-200/50 blur-[2px] rounded-full" />
            <div className="absolute bottom-1/3 right-16 w-2 h-1 bg-orange-300/40 blur-[2px] rounded-full" />
          </div>
          
          {/* Atmospheric edge highlight */}
          <div className="absolute inset-0 rounded-full border-2 border-cyan-300/20 shadow-[inset_0_0_30px_rgba(59,130,246,0.2)]" />
        </div>
      </motion.div>

      {/* Rocket - launching from Earth towards top-left (simplified on mobile) */}
      <motion.div
        className="absolute bottom-40 right-20"
        initial={{ y: 0, x: 0 }}
        animate={isMobile ? {
          // Simpler animation on mobile
          y: [-50, -600],
          x: [0, -300],
        } : {
          // Full animation on desktop
          y: [-100, -1200],
          x: [0, -600],
        }}
        transition={{
          duration: isMobile ? 15 : 20,
          repeat: Infinity,
          ease: 'easeInOut',
          repeatDelay: 5,
        }}
      >
        <svg
          width={isMobile ? "60" : "90"}
          height={isMobile ? "60" : "90"}
          viewBox="0 0 100 100"
          className={`${isMobile ? '' : 'filter drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]'}`}
          style={{ transform: 'rotate(-45deg)' }}
        >
          {/* Rocket Body with gradient */}
          <path
            d="M50 10 L35 60 L50 55 L65 60 Z"
            fill="url(#rocketGradient)"
            stroke="#3b82f6"
            strokeWidth="2"
          />
          
          {/* Rocket Fins */}
          <motion.path
            d="M35 60 L25 75 L35 70 Z"
            fill="#a855f7"
            animate={{
              fill: ['#a855f7', '#9333ea', '#a855f7'],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <motion.path
            d="M65 60 L75 75 L65 70 Z"
            fill="#a855f7"
            animate={{
              fill: ['#a855f7', '#9333ea', '#a855f7'],
            }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
          />
          
          {/* Rocket Window with glow */}
          <circle
            cx="50"
            cy="30"
            r="8"
            fill="#1e293b"
            stroke="#3b82f6"
            strokeWidth="2"
          />
          <motion.circle
            cx="50"
            cy="30"
            r="4"
            fill="#3b82f6"
            animate={{
              opacity: [0.5, 1, 0.5],
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          
          {/* Enhanced Rocket Flame with turbulence */}
          <motion.path
            d="M43 60 L50 85 L57 60"
            fill="url(#flameGradient)"
            animate={{
              d: [
                "M43 60 L50 85 L57 60",
                "M43 60 L48 90 L57 60",
                "M43 60 L52 88 L57 60",
                "M43 60 L50 85 L57 60",
              ],
            }}
            transition={{
              duration: 0.2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          
          {/* Gradients */}
          <defs>
            <linearGradient id="rocketGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="50%" stopColor="#2563eb" />
              <stop offset="100%" stopColor="#1e40af" />
            </linearGradient>
            <linearGradient id="flameGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#f97316" />
              <stop offset="30%" stopColor="#fb923c" />
              <stop offset="60%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#fde047" />
            </linearGradient>
          </defs>
        </svg>
      </motion.div>

      {/* Planet 1 - Purple/Pink (top left, static on mobile) */}
      <motion.div
        className="absolute top-20 left-10 w-32 h-32"
        animate={isMobile ? {} : {
          y: [0, 20, 0],
          rotate: [0, 360],
        }}
        transition={isMobile ? {} : {
          y: { duration: 8, repeat: Infinity, ease: 'easeInOut' },
          rotate: { duration: 100, repeat: Infinity, ease: 'linear' },
        }}
      >
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-400 via-pink-500 to-purple-700 shadow-[inset_-10px_-10px_30px_rgba(0,0,0,0.6)]" />
        {/* Ring */}
        <div className="absolute -inset-8 rounded-full border-4 border-purple-300/30 transform rotate-12" style={{ borderRadius: '50%', borderTop: 'none', borderBottom: 'none' }} />
      </motion.div>

      {/* Planet 2 - Orange/Red (top right, smaller, static on mobile) */}
      <motion.div
        className="absolute top-40 right-32 w-24 h-24"
        animate={isMobile ? {} : {
          y: [0, -15, 0],
          rotate: [0, -360],
        }}
        transition={isMobile ? {} : {
          y: { duration: 10, repeat: Infinity, ease: 'easeInOut' },
          rotate: { duration: 90, repeat: Infinity, ease: 'linear' },
        }}
      >
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-orange-400 via-red-500 to-orange-800 shadow-[inset_-8px_-8px_25px_rgba(0,0,0,0.6)]" />
      </motion.div>

      {/* Moon - small gray (mid-left, static on mobile) */}
      <motion.div
        className="absolute top-1/2 left-1/4 w-16 h-16"
        animate={isMobile ? {} : {
          y: [0, 30, 0],
          x: [0, 20, 0],
          rotate: [0, 360],
        }}
        transition={isMobile ? {} : {
          y: { duration: 12, repeat: Infinity, ease: 'easeInOut' },
          x: { duration: 15, repeat: Infinity, ease: 'easeInOut' },
          rotate: { duration: 80, repeat: Infinity, ease: 'linear' },
        }}
      >
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-gray-300 via-gray-400 to-gray-600 shadow-[inset_-6px_-6px_15px_rgba(0,0,0,0.7)]">
          {/* Craters */}
          <div className="absolute top-2 right-3 w-3 h-3 bg-gray-600/60 rounded-full" />
          <div className="absolute bottom-4 left-2 w-2 h-2 bg-gray-600/60 rounded-full" />
        </div>
      </motion.div>

      {/* Asteroid belt particles (disabled on mobile) */}
      {!isMobile && (
        <motion.div
          className="absolute top-1/3 left-1/2 w-96 h-1"
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 60,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          {[...Array(asteroidCount)].map((_, i) => (
            <div
              key={`asteroid-${i}`}
              className="absolute w-1 h-1 bg-gray-400 rounded-full"
              style={{
                left: `${(i / asteroidCount) * 100}%`,
                top: `${Math.sin(i) * 20}px`,
              }}
            />
          ))}
        </motion.div>
      )}

      {/* Atmospheric glow at bottom (from Earth) */}
      <div className="absolute bottom-0 right-0 w-full h-1/3 bg-gradient-to-t from-blue-500/10 to-transparent" />

      {/* Shooting stars - just the trails (disabled on mobile for performance) */}
      {!isMobile && (
        <>
          <motion.div
            className="absolute top-20 right-1/3"
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={{
              x: [-300, 300],
              y: [0, 300],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 8,
              ease: 'easeIn',
            }}
          >
            <div className="w-32 h-[2px] bg-gradient-to-r from-white to-transparent blur-[1px] -rotate-[135deg]" />
          </motion.div>

          <motion.div
            className="absolute top-1/3 left-1/4"
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={{
              x: [-200, 200],
              y: [0, 200],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatDelay: 12,
              ease: 'easeIn',
            }}
          >
            <div className="w-24 h-[2px] bg-gradient-to-r from-white to-transparent blur-[1px] -rotate-[135deg]" />
          </motion.div>
        </>
      )}

      {/* Subtle vignette */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,5,16,0.5)_100%)]" />
    </div>
  );
}
